import {type CambiosDeExportacion, type CampoEditable} from '@cnc/contracts';
import {useState} from 'react';
import {Link, useNavigate} from 'react-router';
import {FinanciacionDeExportacionSchema} from '@cnc/contracts';
import {ErrorDeApi, pedir} from '../api/cliente';
import {useFinanciadores} from '../api/financiacion';
import {useCatalogosDeSeguimiento, useLoteDeCambios} from '../api/seguimiento';
import {convertirValor, GRUPOS_DE_CAMPOS} from '../componentes/campos-exportacion';
import {EntradaDeCampo} from '../componentes/entrada-de-campo';
import {AYUDA_NUEVA_EXPORTACION} from '../componentes/ayuda-financiacion-bluequest';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {claveDeTexto, tr, trx} from '../i18n/idioma';

// Agregar una exportacion nueva desde el seguimiento. Usa el mismo lote de
// cambios que la edicion (queda en la bitacora como "Editado en la app"):
// primero revisa que el codigo no exista y despues la crea.
export function PaginaNuevaExportacion() {
  const navegar = useNavigate();
  const catalogos = useCatalogosDeSeguimiento();
  const lote = useLoteDeCambios();
  const [valores, setValores] = useState<Partial<Record<CampoEditable, string>>>({});
  const [mensaje, setMensaje] = useState<string | null>(null);
  // Financiador (Daniel, 15/10/2026): se puede dejar en blanco.
  const financiadores = useFinanciadores();
  const [financiador, setFinanciador] = useState('');

  const guardar = async () => {
    setMensaje(null);
    const codigo = (valores.codigo ?? '').trim();
    if (!codigo) {
      setMensaje(tr('Escribe el código de la exportación.', 'Enter the export code.'));
      return;
    }
    const cambios: Record<string, unknown> = {};
    for (const [clave, texto] of Object.entries(valores) as Array<[CampoEditable, string]>) {
      if (clave === 'codigo' || texto.trim() === '') continue;
      const convertido = convertirValor(clave, texto);
      if ('error' in convertido) {
        setMensaje(convertido.error);
        return;
      }
      cambios[clave] = convertido.valor;
    }
    const cuerpo = {
      origen: 'pantalla' as const,
      omitirConErrores: false,
      crearSiNoExiste: true,
      elementos: [{codigo, cambios: cambios as CambiosDeExportacion}],
    };
    try {
      const revision = await lote.mutateAsync({...cuerpo, modo: 'previsualizar'});
      const elemento = revision.elementos[0];
      if (!elemento || elemento.resultado !== 'nueva') {
        setMensaje(
          elemento?.errores.length
            ? elemento.errores.join(' ')
            : tr(
                `Ya existe una exportación con el código ${codigo}. Para cambiarla, ábrela y usa Editar.`,
                `An export with code ${codigo} already exists. To change it, open it and use Edit.`,
              ),
        );
        return;
      }
      const resultado = await lote.mutateAsync({
        ...cuerpo,
        modo: 'aplicar',
        elementos: [{...cuerpo.elementos[0]!, huella: elemento.huella ?? undefined}],
      });
      const creada = resultado.elementos[0];
      if (creada?.errores.length) {
        setMensaje(creada.errores.join(' '));
        return;
      }
      if (financiador)
        await pedir(
          `seguimiento/exportaciones/${encodeURIComponent(creada?.codigoNuevo ?? codigo)}/financiacion`,
          FinanciacionDeExportacionSchema,
          {
            method: 'PUT',
            headers: {'content-type': 'application/json'},
            body: JSON.stringify({version: null, campos: {financiador}}),
          },
        );
      void navegar(`/seguimiento/${encodeURIComponent(creada?.codigoNuevo ?? codigo)}`);
    } catch (error) {
      setMensaje(
        error instanceof ErrorDeApi
          ? [error.message, ...(error.detalles ?? [])].join(' ')
          : tr('No se pudo guardar.', 'Could not save.'),
      );
    }
  };

  return (
    <section className="max-w-5xl">
      <Link to="/seguimiento" className="text-sm text-verde hover:underline">
        {tr('← Volver al seguimiento', '← Back to tracking')}
      </Link>
      <h1 className="mt-3 text-2xl font-semibold text-verde-oscuro">{tr('Nueva exportación', 'New export')}</h1>
      <p className="text-sm text-apagado">
        {tr(
          'Escribe el código y lo que ya sepas; lo demás se puede completar después. Si no eliges exportador, se toma del prefijo del código. Queda en el historial de la exportación.',
          'Enter the code and whatever you already know; the rest can be filled in later. If you do not pick an exporter, it is taken from the code prefix. It is kept in the export history.',
        )}
      </p>
      <form
        className="mt-4 space-y-4"
        onSubmit={e => {
          e.preventDefault();
          void guardar();
        }}
      >
        {GRUPOS_DE_CAMPOS.map(grupo => (
          <fieldset key={claveDeTexto(grupo.titulo)} className="rounded-lg border border-linea bg-white p-5">
            <legend className="px-1 font-medium">{trx(grupo.titulo)}</legend>
            {grupo.nota && <p className="mb-3 text-xs text-apagado">{trx(grupo.nota)}</p>}
            <div className={`grid gap-3 ${grupo.campos.length > 1 ? 'md:grid-cols-2 xl:grid-cols-3' : ''}`}>
              {grupo.campos.map(campo => (
                <EntradaDeCampo
                  key={campo.clave}
                  campo={campo.clave === 'codigo' ? {...campo, ayuda: undefined} : campo}
                  valor={valores[campo.clave] ?? ''}
                  cambiado={(valores[campo.clave] ?? '') !== ''}
                  catalogos={catalogos.data}
                  onCambiar={v => setValores(actual => ({...actual, [campo.clave]: v}))}
                />
              ))}
            </div>
          </fieldset>
        ))}
        <fieldset className="rounded-lg border border-linea bg-white p-5">
          <legend className="px-1 font-medium">{tr('Financiación', 'Financing')}</legend>
          <label className="block max-w-sm text-sm">
            <span className="text-apagado">
              {tr('Financiador', 'Financer')}
              <NotaDeAyuda texto={AYUDA_NUEVA_EXPORTACION.financiador} dentroDeEtiqueta />
            </span>
            <select
              value={financiador}
              onChange={e => setFinanciador(e.target.value)}
              className="mt-1 block w-full rounded-md border border-linea bg-white px-2 py-1.5 text-sm"
            >
              <option value="">{tr('— Sin definir —', '— Not defined —')}</option>
              {(financiadores.data ?? [])
                .filter(f => f.activo)
                .map(f => (
                  <option key={f.codigo} value={f.codigo}>
                    {f.nombre === f.codigo ? f.codigo : `${f.codigo} · ${f.nombre}`}
                  </option>
                ))}
            </select>
          </label>
        </fieldset>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={lote.isPending}
            className="rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
          >
            {lote.isPending ? tr('Guardando…', 'Saving…') : tr('Crear exportación', 'Create export')}
          </button>
          <Link to="/seguimiento" className="rounded-md border border-linea bg-white px-4 py-2 text-sm hover:bg-papel">
            {tr('Cancelar', 'Cancel')}
          </Link>
          {mensaje && (
            <span role="alert" className="text-sm text-alerta">
              {mensaje}
            </span>
          )}
        </div>
      </form>
    </section>
  );
}
